/**
 * Route to Permission Mapping
 * Maps each route path to the required permissions for access
 */

import { NEBULA_INSIGHTS_ENABLED } from './featureFlags';

export interface RoutePermissionConfig {
  path: string;
  requiredPermissions: string[]; // User needs ANY of these permissions
  category?: string; // Permission category for redirect logic
}

/**
 * Route permission configuration
 * Each route can require one or more permissions (user needs ANY of them)
 */
export const ROUTE_PERMISSIONS: RoutePermissionConfig[] = [
  // Dashboard & Analytics
  {
    path: '/dashboard',
    requiredPermissions: ['analytics:view'],
    category: 'analytics'
  },
  {
    path: '/nebula',
    requiredPermissions: ['nebula:view', 'nebula:manage'],
    category: 'analytics'
  },

  // Banking
  {
    path: '/banking',
    requiredPermissions: ['banking:manage'],
    category: 'banking'
  },
  {
    path: '/banking/activities',
    requiredPermissions: ['banking:manage'],
    category: 'banking'
  },

  // Distribution
  {
    path: '/distribution',
    requiredPermissions: ['distribution:manage'],
    category: 'distribution'
  },
  {
    path: '/coming-soon/:feature',
    requiredPermissions: [],
    category: 'content'
  },

  // Profile & Settings
  {
    path: '/profile',
    requiredPermissions: ['profile:view', 'profile:edit'],
    category: 'profile'
  },
  {
    path: '/profile/account',
    requiredPermissions: ['profile:view', 'profile:edit'],
    category: 'profile'
  },
  {
    path: '/profile/change-password',
    requiredPermissions: ['profile:view', 'profile:edit'],
    category: 'profile'
  },
  {
    path: '/profile/artist',
    requiredPermissions: ['profile:view', 'profile:edit'],
    category: 'profile'
  },
  {
    path: '/settings',
    requiredPermissions: ['profile:view', 'profile:edit', 'settings:view', 'settings:edit'],
    category: 'profile'
  },

  // Team Management
  {
    path: '/teams',
    requiredPermissions: ['team:view', 'team:manage'],
    category: 'team'
  },
  {
    path: '/teams/create',
    requiredPermissions: ['team:invite', 'team:manage'],
    category: 'team'
  },
  {
    path: '/teams/:teamId',
    requiredPermissions: ['team:view', 'team:manage'],
    category: 'team'
  },
  {
    path: '/invitations',
    requiredPermissions: ['team:view', 'team:invite', 'team:manage'],
    category: 'team'
  },
  {
    path: '/invitations/accept/:token',
    requiredPermissions: [], // Public invitation acceptance
    category: 'team'
  },
  {
    path: '/admin/invitations',
    requiredPermissions: ['team:manage'],
    category: 'team'
  },
  {
    path: '/admin/roles',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/permissions',
    requiredPermissions: ['team:manage'],
    category: 'team'
  },

  // Content Management
  {
    path: '/merchandise',
    requiredPermissions: ['content:view', 'content:edit'],
    category: 'content'
  },
  {
    path: '/lending',
    requiredPermissions: ['content:view', 'content:edit'],
    category: 'content'
  },

  // Social
  {
    path: '/customer-service',
    requiredPermissions: ['social:view', 'social:manage'],
    category: 'social'
  },

  // Admin Routes (may need system admin check)
  {
    path: '/customer-details',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/account-transactions/:accountId',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/account-transactions-summary',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/account-transactions-mock',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/users-list',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/user-profile/:userId',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/customer-profile/:customerId',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/artist-mapping',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/admin/announcements',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/admin/account-requests',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  ...(NEBULA_INSIGHTS_ENABLED
    ? [
        {
          path: '/admin/nebula-insights',
          requiredPermissions: [], // System admin only - checked separately
          category: 'admin',
        },
      ]
    : []),

  // Payouts Routes (System admin only)
  {
    path: '/payouts/dashboard',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/queue',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/ledger',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/new',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/upload',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/preview',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/select-accounts',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/amount-entry',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/duplicate-warning',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/confirmation',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/queue-submitted',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
  {
    path: '/payouts/transfers/:transactionId',
    requiredPermissions: [], // System admin only - checked separately
    category: 'admin'
  },
];

/**
 * Get permission configuration for a given path
 */
export const getRoutePermissionConfig = (pathname: string): RoutePermissionConfig | null => {
  // Remove query params and hash
  const path = pathname.split('?')[0].split('#')[0];
  
  // Try exact match first
  let config = ROUTE_PERMISSIONS.find(r => r.path === path);
  
  if (config) return config;
  
  // Try pattern matching for dynamic routes
  for (const routeConfig of ROUTE_PERMISSIONS) {
    const routePath = routeConfig.path;
    
    // Check if it's a dynamic route pattern
    if (routePath.includes(':')) {
      const pattern = routePath
        .split('/')
        .map(segment => segment.startsWith(':') ? '[^/]+' : segment)
        .join('/');
      
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(path)) {
        return routeConfig;
      }
    }
  }
  
  return null;
};

/**
 * Category priority order for redirects
 * Routes are checked in this order to find the first accessible route
 */
export const CATEGORY_PRIORITY = [
  'analytics',
  'banking',
  'distribution',
  'profile',
  'team',
  'content',
  'social',
  'admin'
];

/**
 * Default route for each category
 */
export const CATEGORY_DEFAULT_ROUTES: Record<string, string> = {
  'analytics': '/dashboard',
  'banking': '/banking',
  'distribution': '/distribution',
  'profile': '/profile/account',
  'team': '/teams',
  'content': '/merchandise',
  'social': '/customer-service',
  'admin': '/customer-details'
};
